import { DashboardGraphicalConfig } from '../../dashboard/dashboard-graphical';
import * as moment from 'moment';
import {
    RISK_METRIC_CATEGORIES,
} from '@liquitics/transaction';

let contextReferenceDate: string;

export interface RiskGraphicalInput {
    type?: string;
    usage?: string;
    currency?: string;
    account?: string;
    roundedDatetime?: string;
    aggType?: string;
    aggName?: string;
    amount?: number;
    direction?: string;
}

export interface RiskGraphicalOutput {
    xfield: string;
    yfields: string[];
    graphicalOutputData: any[];
}

export interface PrepareGraphicalOutput {
    getSingleDayAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput;
    getStressComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput;
    getComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput;
    getHistoricalTrend(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput;
}

export class PrepareIntradayGraphicalOutput implements PrepareGraphicalOutput {
    intradayGraphicalOutput: RiskGraphicalOutput = {xfield: '', yfields: [], graphicalOutputData: []};
    getSingleDayAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.intradayGraphicalOutput.xfield = 'roundedDatetime';
        this.intradayGraphicalOutput.yfields = ['amount'];
        this.intradayGraphicalOutput.graphicalOutputData = sortByRoundedDatetime(riskGraphicalInput);
        return this.intradayGraphicalOutput;
    }

    getComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.intradayGraphicalOutput.xfield = 'roundedDatetime';
        this.intradayGraphicalOutput.yfields = ['amount0', 'amount1'];
        this.intradayGraphicalOutput.graphicalOutputData = addDeltaField(
            splitTimeByDate(sortByRoundedDatetime(riskGraphicalInput), contextReferenceDate), 'amount0', 'amount1');
        return this.intradayGraphicalOutput;
    }
    getHistoricalTrend(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.intradayGraphicalOutput.xfield = '';
        this.intradayGraphicalOutput.yfields = [];
        this.intradayGraphicalOutput.graphicalOutputData = [];
        return this.intradayGraphicalOutput;
    }
    getStressComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.intradayGraphicalOutput.xfield = 'roundedDatetime';
        this.intradayGraphicalOutput.yfields = ['normal', 'scenario1', 'scenario2', 'scenario3', 'scenario4', 'scenario5'];
        this.intradayGraphicalOutput.graphicalOutputData = getStressedValuesForCompare(riskGraphicalInput);
        return this.intradayGraphicalOutput;
    }
}

export class PrepareGrossPaymentsGraphicalOutput implements PrepareGraphicalOutput {
    grossPaymentsGraphicalOutput: RiskGraphicalOutput = {xfield: '', yfields: [], graphicalOutputData: []};
    getSingleDayAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.grossPaymentsGraphicalOutput.xfield = 'aggName';
        this.grossPaymentsGraphicalOutput.yfields = ['amount'];
        if ((dashboardGraphicalConfig.code = 'GP01') || (dashboardGraphicalConfig.code = 'GP04')) {
            this.grossPaymentsGraphicalOutput.graphicalOutputData = netByKey(absRowAmount(riskGraphicalInput),
                                                                            this.grossPaymentsGraphicalOutput.xfield)
        } else {
            this.grossPaymentsGraphicalOutput.graphicalOutputData = netByKey(absRowAmount(riskGraphicalInput),
                                                                            this.grossPaymentsGraphicalOutput.xfield)
        }
        return this.grossPaymentsGraphicalOutput;
    }

    getComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.grossPaymentsGraphicalOutput.xfield = '';
        this.grossPaymentsGraphicalOutput.yfields = [];
        return this.grossPaymentsGraphicalOutput;
    }
    getHistoricalTrend(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.grossPaymentsGraphicalOutput.xfield = '';
        this.grossPaymentsGraphicalOutput.yfields = ['PAY', 'REC', 'total'];
        this.grossPaymentsGraphicalOutput.graphicalOutputData = splitDateByField(absRowAmount(riskGraphicalInput), contextReferenceDate,
            'direction');
        return this.grossPaymentsGraphicalOutput;
    }
    getStressComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.grossPaymentsGraphicalOutput.xfield = '';
        this.grossPaymentsGraphicalOutput.yfields = [];
        this.grossPaymentsGraphicalOutput.graphicalOutputData = [];
        return this.grossPaymentsGraphicalOutput;
    }
}

export class PrepareThroughputGraphicalOutput implements PrepareGraphicalOutput {

    throughputGraphicalOutput: RiskGraphicalOutput = {xfield: '', yfields: [], graphicalOutputData: []};

    getThroughputForComparison(twoDateThroughputGraphicalRows: any[], referenceDate: string, amountFieldName: string): any[] {
        const referenceDateRows: any[] = this.getThroughput(twoDateThroughputGraphicalRows
        .filter((referenceDateRow: any) => {
            return referenceDateRow.roundedDatetime.startsWith(referenceDate);
        }), amountFieldName);
        const compareDateRows: any[] = this.getThroughput(twoDateThroughputGraphicalRows
        .filter((compareDateRow: any) => {
            return !compareDateRow.roundedDatetime.startsWith(referenceDate);
        }), amountFieldName);
        return sortByRoundedDatetime(compareDateRows.concat(referenceDateRows));
    }

    getThroughput(rowsSortedByDatetime: any[], throughputColumn: string): any[] {
        let totalCashflowForTheDay = 0;
        if ((typeof rowsSortedByDatetime !== 'undefined') && (rowsSortedByDatetime.length > 0)) {
            totalCashflowForTheDay = rowsSortedByDatetime[rowsSortedByDatetime.length - 1].amount;
        }
        if ((typeof rowsSortedByDatetime !== 'undefined') && (rowsSortedByDatetime.length !== 0)) {
            rowsSortedByDatetime.forEach((row) => {
                if (totalCashflowForTheDay !== 0) {
                    row[throughputColumn] = roundAmount(100.0 * row.amount / totalCashflowForTheDay, 2);
                } else {
                    // if no cash flows have been sent or received for this aggregation for the day
                    row[throughputColumn] = 0;
                }
            })
        }
        return rowsSortedByDatetime;
    }

    getThroughputPercentageByDirection(
            throughputGraphicalSingleDayRows: any[], payAmountColumn: string, recAmountColumn: string
        ): any[] {

        const payRows: any[] = this.getThroughput(throughputGraphicalSingleDayRows.filter((r: any) => {
            return (r.direction === 'PAY')
        }), payAmountColumn);
        const recRows: any[] = this.getThroughput(throughputGraphicalSingleDayRows.filter((r: any) => {
            return (r.direction === 'REC')
        }), recAmountColumn);
        console.log(payRows);
        console.log(recRows);
        if ((typeof payRows !== 'undefined') && (payRows.length !== 0)) {
            payRows.forEach((payRow, index: number) => {
            if ((typeof recRows !== 'undefined') && (recRows.length !== 0)) {
                payRow[recAmountColumn] = recRows[index][recAmountColumn];
            } else {
                payRow[recAmountColumn] = 0;
            }
                payRow['delta'] = roundAmount(payRow[payAmountColumn] - payRow[recAmountColumn], 2);
            });
            return payRows;
        } else if ((typeof recRows !== 'undefined') && (recRows.length !== 0)) {
            recRows.forEach((recRow, index: number) => {
                recRow[payAmountColumn] = 0;
                recRow['delta'] = -1 * recRow[recAmountColumn];
            })
            return recRows;
        }
        return throughputGraphicalSingleDayRows;
    }

    groupThroughputTrendRowsByDate(rows: any[], groupField: string): any[] {
        return rows.reduce((grouped, row) => {
            const key = row[groupField];
            delete row['roundedDatetime'];
            if (!grouped[key]) {
              grouped[key] = [];
            }
            grouped[key].push(row);
            return grouped;
          }, {});
    }

    flattenThroughputByTime(rows: any[], amountFieldName: string, roundedDatetime: string): any[] {
        const flattenedThroughputRow = {};
        const flattenedThroughputRows: any[] = [];
        flattenedThroughputRow['roundedDatetime'] = roundedDatetime;
        rows.forEach((row, index: number) => {
            flattenedThroughputRow[row['timeBucket']] = row[amountFieldName];
        });
        flattenedThroughputRow['roundedDatetime'] = moment(roundedDatetime).format('MMM-DD');
        console.log(flattenedThroughputRow);
        flattenedThroughputRows.push(flattenedThroughputRow);
        return flattenedThroughputRows;
    }

    prepareThroughputTrends(rows: any[], amountFieldName: string): any[] {
        rows.forEach((row, index: number) => {
            const datetime = row['roundedDatetime'];
            row['timeBucket'] = moment(datetime).format('HH:mm');
            row['roundedDatetime'] = moment(datetime).format('YYYY-MM-DD');
        });
        const throughputTrends: any[] = [];
        let throughputTrend = {};
        const throughputTrendsByDate = this.groupThroughputTrendRowsByDate(rows, 'roundedDatetime');
        Object.keys(throughputTrendsByDate).forEach((datetime, index: number) => {
            throughputTrend = this.flattenThroughputByTime(this.getThroughput
                (throughputTrendsByDate[datetime], amountFieldName), amountFieldName, datetime)[0];
            throughputTrends.push(throughputTrend);
        });

        return throughputTrends;
    }

    getThroughputTrendYFields(rows: any[]): string[] {
        const yFieldRows = JSON.parse(JSON.stringify(rows));
        yFieldRows.forEach((row, index: number) => {
            const datetime = row['roundedDatetime'];
            row['timeBucket'] = moment(datetime).format('HH:mm');
            row['roundedDatetime'] = moment(datetime).format('YYYY-MM-DD');
        });
        const throughputTrendYFields: string[] = [];
        yFieldRows.forEach((row: any, index: number) => {
            if (!throughputTrendYFields.includes(row['timeBucket'])) {
                throughputTrendYFields.push(row['timeBucket']);
            }
        });
        return throughputTrendYFields;
    }

    getSingleDayAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.throughputGraphicalOutput.xfield = 'roundedDatetime';
        this.throughputGraphicalOutput.yfields = ['Pay', 'Rec'];
        this.throughputGraphicalOutput.graphicalOutputData = this.getThroughputPercentageByDirection(
            sortByRoundedDatetime(riskGraphicalInput), 'Pay', 'Rec');
        return this.throughputGraphicalOutput;
    }

    getComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.throughputGraphicalOutput.xfield = 'roundedDatetime';
        let throughPutDirection = '';
        if (dashboardGraphicalConfig.code.startsWith('OTP')) {
            throughPutDirection = 'Pay';
            this.throughputGraphicalOutput.yfields = ['Pay0', 'Pay1'];
        } else {
            throughPutDirection = 'Rec';
            this.throughputGraphicalOutput.yfields = ['Rec0', 'Rec1'];
        }
        this.throughputGraphicalOutput.graphicalOutputData = addDeltaField(
            splitTimeByDate(
                this.getThroughputForComparison(
                    sortByRoundedDatetime(riskGraphicalInput), contextReferenceDate, throughPutDirection),
                    contextReferenceDate, throughPutDirection),
                    this.throughputGraphicalOutput.yfields[0],
                    this.throughputGraphicalOutput.yfields[1])
        return this.throughputGraphicalOutput;
    }

    getHistoricalTrend(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.throughputGraphicalOutput.xfield = 'roundedDatetime';
        let throughPutDirection = '';
        if (dashboardGraphicalConfig.code.startsWith('OTP')) {
            throughPutDirection = 'Pay';
        } else {
            throughPutDirection = 'Rec';
        }
        this.throughputGraphicalOutput.yfields = this.getThroughputTrendYFields(riskGraphicalInput);
        this.throughputGraphicalOutput.graphicalOutputData = this.prepareThroughputTrends(riskGraphicalInput, throughPutDirection);
        return this.throughputGraphicalOutput;
    }
    getStressComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.throughputGraphicalOutput.xfield = 'roundedDatetime';
        let throughPutDirection = '';
        if (dashboardGraphicalConfig.code.startsWith('OTP')) {
            throughPutDirection = 'Pay';
            this.throughputGraphicalOutput.yfields = ['Pay'];
        } else {
            throughPutDirection = 'Rec';
            this.throughputGraphicalOutput.yfields = ['Rec'];
        }
        this.throughputGraphicalOutput.yfields = ['normal', 'scenario1', 'scenario2', 'scenario3', 'scenario4', 'scenario5'];
        this.throughputGraphicalOutput.graphicalOutputData = getStressedValuesForCompare(
            this.getThroughputPercentageByDirection(
                sortByRoundedDatetime(riskGraphicalInput), 'Pay', 'Rec'), throughPutDirection);
        return this.throughputGraphicalOutput;
    }
}

export class PrepareTimeSpecificGraphicalOutput implements PrepareGraphicalOutput {
    timeSpecificGraphicalOutput: RiskGraphicalOutput = {xfield: '', yfields: [], graphicalOutputData: []};
    getSingleDayAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        if (dashboardGraphicalConfig.code === 'TSP01') {
            this.timeSpecificGraphicalOutput.xfield = 'aggName';
        } else {
            this.timeSpecificGraphicalOutput.xfield = 'roundedDatetime';
        }
        this.timeSpecificGraphicalOutput.yfields = ['amount'];
        this.timeSpecificGraphicalOutput.graphicalOutputData = sortByRoundedDatetime(absRowAmount(riskGraphicalInput));
        return this.timeSpecificGraphicalOutput;
    }

    getComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.timeSpecificGraphicalOutput.xfield = '';
        this.timeSpecificGraphicalOutput.yfields = [];
        this.timeSpecificGraphicalOutput.graphicalOutputData = [];
        return this.timeSpecificGraphicalOutput;
    }
    getHistoricalTrend(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.timeSpecificGraphicalOutput.xfield = '';
        this.timeSpecificGraphicalOutput.yfields = [''];
        this.timeSpecificGraphicalOutput.graphicalOutputData = [];
        return this.timeSpecificGraphicalOutput;
    }
    getStressComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.timeSpecificGraphicalOutput.xfield = '';
        this.timeSpecificGraphicalOutput.yfields = [];
        this.timeSpecificGraphicalOutput.graphicalOutputData = [];
        return this.timeSpecificGraphicalOutput;
    }
}

export class PreparePassthroughGraphicalOutput implements PrepareGraphicalOutput {
    passThroughGraphicalOutput: RiskGraphicalOutput = {xfield: '', yfields: [], graphicalOutputData: []};
    getSingleDayAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        if (dashboardGraphicalConfig.code === 'PTP01') {
            this.passThroughGraphicalOutput.xfield = 'account';
        } else {
            this.passThroughGraphicalOutput.xfield = 'roundedDatetime';
        }
        this.passThroughGraphicalOutput.yfields = ['amount'];
        this.passThroughGraphicalOutput.graphicalOutputData = sortByRoundedDatetime(absRowAmount(riskGraphicalInput));
        return this.passThroughGraphicalOutput;
    }

    getComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.passThroughGraphicalOutput.xfield = '';
        this.passThroughGraphicalOutput.yfields = [];
        this.passThroughGraphicalOutput.graphicalOutputData = [];
        return this.passThroughGraphicalOutput;
    }
    getHistoricalTrend(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.passThroughGraphicalOutput.xfield = 'roundedDatetime';
        this.passThroughGraphicalOutput.yfields = ['amount'];
        this.passThroughGraphicalOutput.graphicalOutputData = splitDateByField(
            absRowAmount(
                formatRoundedDatetimeToDate(riskGraphicalInput)), contextReferenceDate, 'direction');
        return this.passThroughGraphicalOutput;
    }
    getStressComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.passThroughGraphicalOutput.xfield = '';
        this.passThroughGraphicalOutput.yfields = [];
        this.passThroughGraphicalOutput.graphicalOutputData = [];
        return this.passThroughGraphicalOutput;
    }
}

export class PrepareNetBreachGraphicalOutput implements PrepareGraphicalOutput {
    netBreachGraphicalOutput: RiskGraphicalOutput = {xfield: '', yfields: [], graphicalOutputData: []};
    getNetBreachAnalysis(riskGraphicalInput: RiskGraphicalInput[]): any[] {
        const referenceDateRows = splitXAxisFieldByField(absRowAmount
                                    (
                                        riskGraphicalInput.filter((referenceDateRow: any) => {
                                                return referenceDateRow.roundedDatetime.startsWith(contextReferenceDate);
                                            })),
                                         'aggName', 'direction'
                                    );
        const compareDateRows = splitXAxisFieldByField(absRowAmount
                                    (
                                        riskGraphicalInput.filter((referenceDateRow: any) => {
                                                return !referenceDateRow.roundedDatetime.startsWith(contextReferenceDate);
                                            })),
                                            'aggName', 'direction'
                                    );
        referenceDateRows.forEach((referenceDateRow, index: number) => {
            referenceDateRow['PAY1'] = referenceDateRow['PAY'];
            referenceDateRow['REC1'] = referenceDateRow['REC'];
        });
        compareDateRows.forEach((compareDateRow, index: number) => {
            compareDateRow['PAY0'] = compareDateRow['PAY'];
            compareDateRow['REC0'] = compareDateRow['REC'];
        });
        referenceDateRows
            .sort((r1: any, r2: any) => {
                return r1.aggName.toString().localeCompare(r2.aggName.toString());
            });
        compareDateRows
            .sort((r1: any, r2: any) => {
                return r1.aggName.toString().localeCompare(r2.aggName.toString());
            });
        if ((typeof compareDateRows !== 'undefined') && (compareDateRows.length !== 0)) {
            referenceDateRows.forEach((referenceDateRow, index: number) => {
                referenceDateRow['PAY0'] = compareDateRows[index]['PAY0'];
                referenceDateRow['REC0'] = compareDateRows[index]['REC0'];
            });
        } else {
            referenceDateRows.forEach((referenceDateRow, index: number) => {
                referenceDateRow['PAY0'] = 0;
                referenceDateRow['REC0'] = 0;
            });
        }
        console.log(referenceDateRows);
        return referenceDateRows;
    }
    getSingleDayAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.netBreachGraphicalOutput.xfield = 'aggName';
        this.netBreachGraphicalOutput.yfields = ['PAY', 'REC'];
        this.netBreachGraphicalOutput.graphicalOutputData = splitXAxisFieldByField(
            absRowAmount(riskGraphicalInput), 'aggName', 'direction');
        return this.netBreachGraphicalOutput;
    }

    getComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.netBreachGraphicalOutput.xfield = 'aggName';
        this.netBreachGraphicalOutput.yfields = ['PAY0', 'PAY1', 'REC0', 'REC1'];
        this.netBreachGraphicalOutput.graphicalOutputData = this.getNetBreachAnalysis(riskGraphicalInput);
        console.log(this.netBreachGraphicalOutput.graphicalOutputData);
        return this.netBreachGraphicalOutput;
    }
    getHistoricalTrend(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.netBreachGraphicalOutput.xfield = 'roundedDatetime';
        this.netBreachGraphicalOutput.yfields = ['amount'];
        this.netBreachGraphicalOutput.graphicalOutputData = formatRoundedDatetimeToDate(sortByRoundedDatetime(riskGraphicalInput));
        return this.netBreachGraphicalOutput;
    }
    getStressComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.netBreachGraphicalOutput.xfield = '';
        this.netBreachGraphicalOutput.yfields = [''];
        this.netBreachGraphicalOutput.graphicalOutputData = [];
        return this.netBreachGraphicalOutput;
    }
}

export class PrepareSurvivalHorizonGraphicalOutput implements PrepareGraphicalOutput {
    survivalHorizonGraphicalOutput: RiskGraphicalOutput = {xfield: '', yfields: [], graphicalOutputData: []};
    sortSHGraphicalRowsDataByTenor(riskGraphicalInput: RiskGraphicalInput[]): any[] {
        const filteredrows = riskGraphicalInput.filter((row: any ) => {
            return (row['aggName'].toString().substring(row['aggName'].toString().length - 1, row['aggName'].toString().length) === 'D')
        });
        console.log(filteredrows);
        filteredrows.sort((r1: any, r2: any) => {
            return(Number(r1['aggName'].toString().substring(0, r1['aggName'].toString().length - 1)) -
            Number(r2['aggName'].toString().substring(0, r2['aggName'].toString().length - 1)));
        });
        return filteredrows;
    }

    compareSurvivalHorizon(riskGraphicalInput: RiskGraphicalInput[]): any[] {
        const referenceDateRows = this.sortSHGraphicalRowsDataByTenor(riskGraphicalInput.filter((referenceDateRow: any) => {
            return referenceDateRow.roundedDatetime.startsWith(contextReferenceDate);
        }));

        const compareDateRows = this.sortSHGraphicalRowsDataByTenor(riskGraphicalInput.filter((compareDateRow: any) => {
            return !compareDateRow.roundedDatetime.startsWith(contextReferenceDate);
        }));
        if ((typeof compareDateRows !== 'undefined') && (compareDateRows.length !== 0)) {
            referenceDateRows.forEach((referenceDateRow, index: number) => {
                referenceDateRow['amount0'] = referenceDateRow['amount'];
                referenceDateRow['amount1'] = compareDateRows[index]['amount'];
                referenceDateRow['delta'] = referenceDateRow['amount'] - compareDateRows[index]['amount'];
            });
        } else {
            referenceDateRows.forEach((referenceDateRow, index: number) => {
                referenceDateRow['amount0'] = referenceDateRow['amount'];
                referenceDateRow['amount1'] = referenceDateRow['amount'];
                referenceDateRow['delta'] = 0;
            });
        }
        return referenceDateRows;
    }

    getSingleDayAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.survivalHorizonGraphicalOutput.xfield = 'aggName';
        this.survivalHorizonGraphicalOutput.yfields = ['amount'];
        this.survivalHorizonGraphicalOutput.graphicalOutputData = this.sortSHGraphicalRowsDataByTenor(riskGraphicalInput);
        return this.survivalHorizonGraphicalOutput;
    }

    getComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.survivalHorizonGraphicalOutput.xfield = 'aggName';
        this.survivalHorizonGraphicalOutput.yfields = ['amount0', 'amount1'];
        this.survivalHorizonGraphicalOutput.graphicalOutputData = this.compareSurvivalHorizon(riskGraphicalInput);
        return this.survivalHorizonGraphicalOutput;
    }
    getHistoricalTrend(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.survivalHorizonGraphicalOutput.xfield = 'roundedDatetime';
        this.survivalHorizonGraphicalOutput.yfields = ['amount'];
        this.survivalHorizonGraphicalOutput.graphicalOutputData = sortByRoundedDatetime(formatRoundedDatetimeToDate(riskGraphicalInput));
        return this.survivalHorizonGraphicalOutput;
    }
    getStressComparisonAnalysis(riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig): RiskGraphicalOutput {
        this.survivalHorizonGraphicalOutput.xfield = '';
        this.survivalHorizonGraphicalOutput.yfields = [''];
        this.survivalHorizonGraphicalOutput.graphicalOutputData = [];
        return this.survivalHorizonGraphicalOutput;
    }
}


export function getSingleDayAnalysisForGraphical(
    riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig, referenceDate: string): RiskGraphicalOutput {
    let prepareGraphicalOutput: PrepareGraphicalOutput;
    contextReferenceDate = referenceDate;
    switch (dashboardGraphicalConfig.category) {
        case RISK_METRIC_CATEGORIES.IntradayBalance:
            prepareGraphicalOutput = new PrepareIntradayGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.GrossPayments:
            prepareGraphicalOutput = new PrepareGrossPaymentsGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.TimeSpecificPayments:
            prepareGraphicalOutput = new PrepareTimeSpecificGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.PassThroughPayments:
            prepareGraphicalOutput = new PreparePassthroughGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.Throughput:
            prepareGraphicalOutput = new PrepareThroughputGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.SurvivalHorizon:
            prepareGraphicalOutput = new PrepareSurvivalHorizonGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.NetBreach:
            prepareGraphicalOutput = new PrepareNetBreachGraphicalOutput;
            break;
    }
    return prepareGraphicalOutput.getSingleDayAnalysis(riskGraphicalInput, dashboardGraphicalConfig);
}

export function getCompareAnalysisForGraphical(
    riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig, referenceDate: string): RiskGraphicalOutput {
    let prepareGraphicalOutput: PrepareGraphicalOutput;
    contextReferenceDate = referenceDate;
    switch (dashboardGraphicalConfig.category) {
        case RISK_METRIC_CATEGORIES.IntradayBalance:
            prepareGraphicalOutput = new PrepareIntradayGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.GrossPayments:
            prepareGraphicalOutput = new PrepareGrossPaymentsGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.TimeSpecificPayments:
            prepareGraphicalOutput = new PrepareTimeSpecificGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.PassThroughPayments:
            prepareGraphicalOutput = new PreparePassthroughGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.Throughput:
            prepareGraphicalOutput = new PrepareThroughputGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.SurvivalHorizon:
            prepareGraphicalOutput = new PrepareSurvivalHorizonGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.NetBreach:
            prepareGraphicalOutput = new PrepareNetBreachGraphicalOutput;
            break;
    }
    return prepareGraphicalOutput.getComparisonAnalysis(riskGraphicalInput, dashboardGraphicalConfig);
}

export function getHistoricalTrendForGraphical(
    riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig, referenceDate: string): RiskGraphicalOutput {
    let prepareGraphicalOutput: PrepareGraphicalOutput;
    contextReferenceDate = referenceDate;
    switch (dashboardGraphicalConfig.category) {
        case RISK_METRIC_CATEGORIES.IntradayBalance:
            prepareGraphicalOutput = new PrepareIntradayGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.GrossPayments:
            prepareGraphicalOutput = new PrepareGrossPaymentsGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.TimeSpecificPayments:
            prepareGraphicalOutput = new PrepareTimeSpecificGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.PassThroughPayments:
            prepareGraphicalOutput = new PreparePassthroughGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.Throughput:
            prepareGraphicalOutput = new PrepareThroughputGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.SurvivalHorizon:
            prepareGraphicalOutput = new PrepareSurvivalHorizonGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.NetBreach:
            prepareGraphicalOutput = new PrepareNetBreachGraphicalOutput;
            break;
    }
    return prepareGraphicalOutput.getHistoricalTrend(riskGraphicalInput, dashboardGraphicalConfig);
}

export function getStressComparisonAnalysis(
    riskGraphicalInput: RiskGraphicalInput[], dashboardGraphicalConfig: DashboardGraphicalConfig, referenceDate: string): RiskGraphicalOutput {
    console.log('I am inside stress comparison ' + dashboardGraphicalConfig.category.identifier);
    let prepareGraphicalOutput: PrepareGraphicalOutput;
    contextReferenceDate = referenceDate;
    switch (dashboardGraphicalConfig.category) {
        case RISK_METRIC_CATEGORIES.IntradayBalance:
            console.log('This is intraday balance');
            prepareGraphicalOutput = new PrepareIntradayGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.GrossPayments:
            prepareGraphicalOutput = new PrepareGrossPaymentsGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.TimeSpecificPayments:
            prepareGraphicalOutput = new PrepareTimeSpecificGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.PassThroughPayments:
            prepareGraphicalOutput = new PreparePassthroughGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.Throughput:
            prepareGraphicalOutput = new PrepareThroughputGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.SurvivalHorizon:
            prepareGraphicalOutput = new PrepareSurvivalHorizonGraphicalOutput;
            break;
        case RISK_METRIC_CATEGORIES.NetBreach:
            prepareGraphicalOutput = new PrepareNetBreachGraphicalOutput;
            break;
    }
    return prepareGraphicalOutput.getStressComparisonAnalysis(riskGraphicalInput, dashboardGraphicalConfig);
}

function absRowAmount(rows: any[]): any[] {
    return rows
        .map((r: any) => {
            r.amount = Math.abs(r.amount);
            return r;
        });
}

function netByKey(rows: any[], groupField: string): any[] {
    const rowsByKey = rows
        .reduce((grouped, row) => {
            const key = row[groupField];
            grouped[key] = grouped[key] || [];
            grouped[key].push(row);
            return grouped;
        }, {});
    return Object.keys(rowsByKey)
        .map((key: string) => {
            return rowsByKey[key].reduce((prev, curr) => {
                prev.amount += curr.amount;
                return prev;
            });
        });
}

function sortByRoundedDatetime(rows: any[]): any[] {
    return rows
        .sort((r1: any, r2: any) => {
            return moment(r1.roundedDatetime).diff(moment(r2.roundedDatetime));
        });
}

function roundAmount(amount: number, noofDecimals: number) {
    return Math.round((amount) * Math.pow(10, noofDecimals)) / Math.pow(10, noofDecimals);
}

function splitTimeByDate(rows: any[], referenceDate: string, amountFieldName ?: string): any[] {
    if (!amountFieldName) {
        amountFieldName = 'amount';
    }
    return splitTimeByKey(rows, referenceDate, (row) => {
        const datetime = row['roundedDatetime'];
        const key = moment(datetime).format('YYYY-MM-DD');
        return key;
    }, amountFieldName)
}

function splitTimeByField(rows: any[], referenceDate: string, field: string): any[] {
    return splitTimeByKey(rows, referenceDate, (row) => {
        return row[field];
    });
}

function splitTimeByKey(rows: any[], referenceDate: string, keyExtractor: (row) => string, amountFieldName?: string): any[] {
    if (!amountFieldName) {
        amountFieldName = 'amount';
    }
    return splitXAxisByKey(rows,
        (row) => {
            const datetime = row['roundedDatetime'];
            const time = moment(datetime).format('HH:mm');
            return time;
        },
        'roundedDatetime',
        (xValue) => {
            return referenceDate + ' ' + xValue;
        },
        keyExtractor,
        amountFieldName);
}

function splitDateByField(rows: any[], referenceDate: string, field: string, amountFieldName?: string): any[] {
    if (!amountFieldName) {
        amountFieldName = 'amount';
    }
    return splitDateByKey(rows, referenceDate, (row) => {
        return row[field];
    }, amountFieldName);
}

function splitDateByKey(rows: any[], referenceDate: string, keyExtractor: (row) => string, amountFieldName?: string): any[] {
    if (!amountFieldName) {
        amountFieldName = 'amount';
    }
    return splitXAxisByKey(rows,
        (row) => {
            const datetime = row['roundedDatetime'];
            const time = moment(datetime).format('YYYY-MM-DD');
            return time;
        },
        'roundedDatetime',
        (xValue) => {
            return xValue;
        },
        keyExtractor,
        amountFieldName
    );
}

function splitXAxisFieldByKey(rows: any[], xAxisField: string, keyExtractor: (row) => string): any[] {
    return splitXAxisByKey(rows,
        (row) => {
            return row[xAxisField];
        },
        xAxisField,
        (xValue) => {
            return xValue;
        },
        keyExtractor);
}

function splitXAxisFieldByField(rows: any[], xAxisField: string, field: string): any[] {
    return splitXAxisByKey(rows,
        (row) => {
            return row[xAxisField];
        },
        xAxisField,
        (xValue) => {
            return xValue;
        },
        (row) => {
            return row[field];
        });
}

function splitXAxisByKey(rows: any[],
    xAxisExtractor: (row) => string,
    xAxisFieldName: string,
    xAxisGenerator: (xValue) => string,
    keyExtractor: (row) => string,
    amountFieldName?: string): any[] {
    const byXAxis = rows.reduce((grouped, row) => {
        const xAxis = xAxisExtractor(row);
        grouped[xAxis] = grouped[xAxis] || [];
        grouped[xAxis].push(row);
        return grouped;
    }, {});
    if (!amountFieldName) {
        amountFieldName = 'amount';
    }
    return Object.keys(byXAxis)
        .map((xValue: string) => {
            const newRow = {};
            newRow[xAxisFieldName] = xAxisGenerator(xValue);
            const byKey = byXAxis[xValue].reduce((grouped, row) => {
                const key = keyExtractor(row);
                grouped[key] = grouped[key] || [];
                grouped[key].push(row);
                return grouped;
            }, {});
            let total = 0;
            const keyCount = Object.keys(byKey).length;
            Object.keys(byKey)
                .forEach((key: string, keyIndex: number) => {
                    byKey[key].forEach((row, rowIndex: number) => {
                        const amount = row[amountFieldName];
                        const roundedDatetime = row['roundedDatetime'];
                        let newRowKey = amountFieldName;
                        if (keyCount > 1) {
                            newRowKey += keyIndex;
                        }
                        newRow[newRowKey] = amount;
                        newRow[key] = amount;
                        newRow['roundedDatetime'] = roundedDatetime;
                        if (amountFieldName === 'amount') {
                            total += amount;
                        }
                    });
                });
                if (amountFieldName === 'amount') {
                    newRow['total'] = total;
                }
            return newRow;
        });
}

function addDeltaField(rows: any[], col1: string, col2: string): any[] {
    return rows.map((row) => {
        row['delta'] = row[col1] - row[col2];
        return row;
    })
}

function formatRoundedDatetimeToDate(rows: any[]): any[] {
    rows.forEach((row, index: number) => {
        const datetime = row['roundedDatetime'];
        row['roundedDatetime'] = moment(datetime).format('YYYY-MM-DD');
    });
    return rows;
}

function groupRiskDataByField(rows: any[], groupField: string): any[] {
    return rows.reduce((grouped, row) => {
        const key = row[groupField];
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(row);
        return grouped;
      }, {});
}

function flattenRiskDataByXField(rows: any[], xFieldName: string, amountFieldName: string, roundedDatetime: string): any[] {
    const flattenedThroughputRow = {};
    const flattenedThroughputRows: any[] = [];
    flattenedThroughputRow['roundedDatetime'] = moment(roundedDatetime).format('HH:mm');
    rows.forEach((row, index: number) => {
        flattenedThroughputRow[row[xFieldName]] = row[amountFieldName];
    });
    // flattenedThroughputRow['roundedDatetime'] = moment(roundedDatetime).format('MMM-DD');
    flattenedThroughputRows.push(flattenedThroughputRow);
    return flattenedThroughputRows;
}

function getStressedValuesForCompare(rows: any[], amountFieldName?: string): any[] {
    if (!amountFieldName) {
        amountFieldName = 'amount';
    }
    rows.forEach((row, index: number) => {
        const amount = row['amount'];
        row[row['scenario']] = amount;
    });
    const stressedCompareRiskDataRows: any[] = [];
    let stressCompareRow = {};
    const riskDataGroupedByDatetime = groupRiskDataByField(rows, 'roundedDatetime');
    Object.keys(riskDataGroupedByDatetime).forEach((dateTime, index: number) => {
        stressCompareRow = flattenRiskDataByXField(riskDataGroupedByDatetime[dateTime],
           'scenario' , amountFieldName, dateTime)[0];
        stressedCompareRiskDataRows.push(stressCompareRow);
    });
    return stressedCompareRiskDataRows;
}
